/* eslint-disable max-len */
import {
    isResponseError,
    mapInChunks,
    Utilities,
} from './../../../helpers/utils';
import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { RootState } from '../..';
import {
    selectAppConfig,
    selectReplyToId,
    selectStubUrl,
} from '../../selectors/app';
import { selectUser, selectUserId } from '../../selectors/auth';
import {
    OperationId,
    setFilesToUpload,
    showNotification,
    startFetch,
    stopFetch,
    updateFetchMessage,
} from '../app/actionCreators';
import { AppErrorCode, NotificationStatus } from '../app/actionTypes';
import {
    ItemType,
    SharePermissions,
    ShareType,
    nextcloudApi,
} from '@sendent/nextcloud-api';
import { createShareFolders } from '../app/actions';
import { FileItem } from '../../../model';
import { selectTranslate } from '../../selectors/ui';
import { batch } from 'react-redux';
import {
    DndEmailShareDialogResult,
    DndPublicShareDialogResult,
    DndShareWithCirclesDialogResult,
    SharedFileInfo,
    submitDialogResult,
} from '../../../services/dialogResults';
import { selectTeamsContext } from '../../selectors/conversation';
import { ResolvedChatMember } from '../../reducers/conversation/reducer';
import { mapPlainFilesToFileItems } from '../../../helpers/file';
import { uploadFilesInParallel } from '../../../services/file';
import { Circle } from '../../../services/microsoft/msGraphApi';
import { MEGABYTE, SHARE_REQUEST_CHUNK_SIZE } from '../../../helpers/constants';
import { logger } from '../../../helpers/logger';
import {
    DndReceiveError,
    DndReceiveProgress,
    describeDndReceive,
    receiveDndFiles,
} from '../../../helpers/dndFileReceiver';
import { formatBytes } from '../../../helpers/fileExplorer';

const DND_RECEIVE_PROGRESS_INTERVAL_MS = 500;

// Teams hands the dropped files over once, so request them only once
let dndFilesRequested = false;

export const loadDndFilesThunk = (): ThunkAction<
    Promise<void>,
    RootState,
    unknown,
    AnyAction
> => {
    return async function thunk(dispatch, getState): Promise<void> {
        const state = getState();
        const translate = selectTranslate(state);
        const context = selectTeamsContext(state);
        if (!context) {
            logger.error('No teams context found', 'Teams');
            return;
        }
        if (dndFilesRequested) {
            return;
        }
        dndFilesRequested = true;

        dispatch(
            startFetch({
                id: OperationId.LoadDndFiles,
                message: translate('dnd_files_receiving'),
            }),
        );

        const replyToId = selectReplyToId(state);
        const inputThreadId = context.channel?.id || context.chat?.id || '';
        const replyToIdForThreads = replyToId || '';
        const uniqueId = replyToIdForThreads + inputThreadId;

        let lastProgressAt = 0;
        const onProgress = ({
            receivedBytes,
            fileName,
        }: DndReceiveProgress): void => {
            const now = Date.now();
            if (now - lastProgressAt < DND_RECEIVE_PROGRESS_INTERVAL_MS) {
                return;
            }
            lastProgressAt = now;
            // The host never announces the total size, so only the running count can be shown
            const progress = fileName
                ? `${fileName} (${formatBytes(receivedBytes, 0)})`
                : formatBytes(receivedBytes, 0);
            dispatch(
                updateFetchMessage({
                    operationId: OperationId.LoadDndFiles,
                    message: `${translate('dnd_files_receiving')} ${progress}`,
                }),
            );
        };

        let files: File[];
        try {
            const received = await receiveDndFiles({ uniqueId, onProgress });
            files = received.files;
            logger.info(
                `getDragAndDropFiles: received ${describeDndReceive(received.stats)}`,
                'Files',
            );
        } catch (err) {
            const reason = err instanceof Error ? err.message : String(err);
            const details =
                err instanceof DndReceiveError
                    ? describeDndReceive(err.stats)
                    : '';
            logger.error(
                `getDragAndDropFiles: ${reason} (${details})`,
                'Files',
            );
            // Technical details stay in the notification so support tickets carry them
            dispatch(
                stopFetch({
                    operationId: OperationId.LoadDndFiles,
                    message: `${translate('dnd_files_receive_error')} (${details})`,
                    errorCode: AppErrorCode.CustomError,
                    status: NotificationStatus.Error,
                }),
            );
            return;
        }

        // Large files stay File objects and are streamed in chunks at upload time
        const minChunkedUploadFileSizeBytes =
            selectAppConfig(getState()).minChunkedUploadFileSizeMb * MEGABYTE;
        const fileContentArr = await Promise.all(
            files.map((file) =>
                file.size < minChunkedUploadFileSizeBytes
                    ? file.arrayBuffer()
                    : Promise.resolve(undefined),
            ),
        );

        batch(() => {
            dispatch(
                setFilesToUpload(
                    mapPlainFilesToFileItems(files, fileContentArr),
                ),
            );
            dispatch(stopFetch({ operationId: OperationId.LoadDndFiles }));
        });
    };
};

export const shareDndFilesThunk = ({
    files,
    folder,
    password,
    expirationDate,
    onUploadProgress = () => {},
}: {
    files: FileItem[];
    folder: string;
    password?: string;
    expirationDate?: Date | null;
    onUploadProgress?: (progress: number) => void;
}): ThunkAction<Promise<void>, RootState, unknown, AnyAction> => {
    return async function thunk(dispatch, getState): Promise<void> {
        const state = getState();
        const user = selectUser(state);
        const userId = selectUserId(state);
        const stubUrl = selectStubUrl(state);
        const translate = selectTranslate(state);
        if (!user || !userId || !stubUrl) {
            return;
        }

        const isFolderCreated = await createShareFolders(folder);

        if (!isFolderCreated) {
            dispatch(
                showNotification({
                    message: translate('could_not_create_folder'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );
            return;
        }

        const requestConfig = {
            stubUrl,
            user,
            userId,
        } as const;

        const { uploadResponses, finalFilenames } = await uploadFilesInParallel(
            {
                files,
                folder,
                chunkSizeBytes:
                    state.appReducer.appConfig.chunkSizeMb * MEGABYTE,
                onUploadProgress,
                requestConfig,
            },
        );

        if (uploadResponses.some(isResponseError)) {
            logger.error(
                `uploadResults: ${JSON.stringify(uploadResponses)}`,
                'Upload',
            );

            dispatch(
                showNotification({
                    message: translate('upload_selected_error'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );

            if (uploadResponses.every(isResponseError)) {
                return;
            }
        }

        const expirationDateStr = Utilities.formatUploadDate(expirationDate);
        const uploadedFilenames = finalFilenames.filter(
            (filename, idx) => uploadResponses[idx].success,
        );
        const shareResponses = await Promise.all(
            uploadedFilenames.map((filename) =>
                nextcloudApi.shareByPath({
                    requestConfig,
                    shareTargetPath: `${folder}/${filename}`,
                    shareType: ShareType.PublicLink,
                    permissions: SharePermissions.Read,
                    password,
                    expirationDate: expirationDateStr,
                }),
            ),
        );

        if (shareResponses.some(isResponseError)) {
            logger.error(
                `shareResponses: ${JSON.stringify(shareResponses)}`,
                'Share',
            );

            dispatch(
                showNotification({
                    message: translate('share_selected_error'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );

            if (shareResponses.every(isResponseError)) {
                return;
            }
        }

        const sharedFileInfoArr = [];
        for (let i = 0; i < shareResponses.length; i++) {
            const shareRes = shareResponses[i];
            if (!shareRes.success) {
                continue;
            }

            sharedFileInfoArr.push({
                name: uploadedFilenames[i],
                url: shareRes.result.ocs.data.url,
            });
        }

        submitDialogResult(
            new DndPublicShareDialogResult({
                sharedFileInfoArr,
                password,
                expirationDate: expirationDateStr,
            }),
        );

        if (shareResponses.some((res) => !res.success)) {
            dispatch(
                showNotification({
                    message: translate('upload_selected_error'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );
        }
    };
};

export const shareDndFilesWithCircleThunk = ({
    files,
    folder,
    selectedCircle,
    password,
    expirationDate,
    onUploadProgress = () => {},
}: {
    files: FileItem[];
    folder: string;
    selectedCircle: Circle;
    password?: string;
    expirationDate?: Date | null;
    onUploadProgress?: (progress: number) => void;
}): ThunkAction<Promise<void>, RootState, unknown, AnyAction> => {
    return async function thunk(dispatch, getState): Promise<void> {
        const state = getState();
        const user = selectUser(state);
        const userId = selectUserId(state);
        const stubUrl = selectStubUrl(state);
        const translate = selectTranslate(state);
        const teamsContext = selectTeamsContext(state);
        const isChannelOrTeam = Boolean(
            teamsContext?.channel?.id || teamsContext?.team?.groupId,
        );
        if (!user || !userId || !stubUrl) {
            return;
        }

        const isFolderCreated = await createShareFolders(folder);

        if (!isFolderCreated) {
            dispatch(
                showNotification({
                    message: translate('could_not_create_folder'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );
            return;
        }

        const requestConfig = {
            stubUrl,
            user,
            userId,
        } as const;

        const { uploadResponses, finalFilenames } = await uploadFilesInParallel(
            {
                files,
                folder,
                chunkSizeBytes:
                    state.appReducer.appConfig.chunkSizeMb * MEGABYTE,
                onUploadProgress,
                requestConfig,
            },
        );

        if (uploadResponses.some(isResponseError)) {
            logger.error(
                `uploadResults: ${JSON.stringify(uploadResponses)}`,
                'Upload',
            );

            dispatch(
                showNotification({
                    message: translate('upload_selected_error'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );

            if (uploadResponses.every(isResponseError)) {
                return;
            }
        }

        const expirationDateStr = Utilities.formatUploadDate(expirationDate);
        const uploadedFilenames = finalFilenames.filter(
            (filename, idx) => uploadResponses[idx].success,
        );

        if (isChannelOrTeam && selectedCircle) {
            const circleShareResponses = await Promise.all(
                uploadedFilenames.map((filename) => {
                    return nextcloudApi.shareByPath({
                        requestConfig,
                        shareTargetPath: `${folder}/${filename}`,
                        shareType: ShareType.Circle,
                        shareWith: selectedCircle.id,
                        permissions: SharePermissions.Read,
                        password,
                        expirationDate: expirationDateStr,
                    });
                }),
            );

            if (circleShareResponses.every(isResponseError)) {
                return;
            }

            if (circleShareResponses.some(isResponseError)) {
                logger.error(
                    `shareResponses: ${JSON.stringify(circleShareResponses)}`,
                    'Share',
                );

                dispatch(
                    showNotification({
                        message: translate('share_selected_error'),
                        status: NotificationStatus.Error,
                        errorCode: AppErrorCode.CustomError,
                    }),
                );
            }

            const sharedFileInfoArr = [];
            for (let i = 0; i < circleShareResponses.length; i++) {
                const shareRes = circleShareResponses[i];
                if (!shareRes.success) {
                    continue;
                }

                sharedFileInfoArr.push({
                    name: uploadedFilenames[i],
                    url: `${user.server}/index.php/f/${shareRes.result.ocs.data.file_source}`,
                });
            }

            submitDialogResult(
                new DndShareWithCirclesDialogResult({
                    sharedFileInfoArr,
                }),
            );
            return;
        }
    };
};

export const emailShareDndFilesThunk = ({
    files,
    folder,
    email,
    permissions,
    onUploadProgress = () => {},
}: {
    files: FileItem[];
    folder: string;
    email: string;
    permissions: SharePermissions;
    onUploadProgress?: (progress: number) => void;
}): ThunkAction<Promise<void>, RootState, unknown, AnyAction> => {
    return async function thunk(dispatch, getState): Promise<void> {
        const state = getState();
        const user = selectUser(state);
        const userId = selectUserId(state);
        const stubUrl = selectStubUrl(state);
        const translate = selectTranslate(state);
        if (!user || !userId || !stubUrl) {
            return;
        }

        const shareesRes = await nextcloudApi.searchSharees({
            requestConfig: {
                stubUrl,
                user,
            },
            searchTerm: email,
            itemType: ItemType.Folder,
            shareTypes: [ShareType.User],
        });

        if (!shareesRes.success) {
            dispatch(
                showNotification({
                    errorCode: AppErrorCode.CustomError,
                    message: translate('something_went_wrong'),
                    status: NotificationStatus.Error,
                }),
            );
            return;
        }

        const sharee = shareesRes.result.ocs.data.exact.users[0];

        if (!sharee) {
            dispatch(
                stopFetch({
                    operationId: OperationId.EmailShare,
                    errorCode: AppErrorCode.CustomError,
                    message: translate('something_went_wrong'),
                    status: NotificationStatus.Error,
                }),
            );
            return;
        }

        const isFolderCreated = await createShareFolders(folder);

        if (!isFolderCreated) {
            dispatch(
                showNotification({
                    message: translate('could_not_create_folder'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );
            return;
        }

        const requestConfig = {
            stubUrl,
            user,
            userId,
        } as const;

        const { uploadResponses, finalFilenames } = await uploadFilesInParallel(
            {
                files,
                folder,
                chunkSizeBytes:
                    state.appReducer.appConfig.chunkSizeMb * MEGABYTE,
                onUploadProgress,
                requestConfig,
            },
        );

        if (uploadResponses.some(isResponseError)) {
            logger.error(
                `uploadResults: ${JSON.stringify(uploadResponses)}`,
                'Upload',
            );
            dispatch(
                showNotification({
                    message: translate('upload_selected_error'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );

            if (uploadResponses.every(isResponseError)) {
                return;
            }
        }

        const uploadedFilenames = finalFilenames.filter(
            (filename, idx) => uploadResponses[idx].success,
        );
        const shareResponses = await Promise.all(
            uploadedFilenames.map((filename) =>
                nextcloudApi.shareByPath({
                    shareTargetPath: `${folder}/${filename}`,
                    shareType: sharee.value.shareType,
                    shareWith: sharee.value.shareWith,
                    permissions: permissions,
                    requestConfig,
                }),
            ),
        );

        if (shareResponses.some(isResponseError)) {
            logger.error(
                `shareResponses: ${JSON.stringify(shareResponses)}`,
                'Share',
            );
            dispatch(
                showNotification({
                    message: translate('share_selected_error'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );

            if (shareResponses.every(isResponseError)) {
                return;
            }
        }

        const sharedFileInfoArr: SharedFileInfo[] = [];
        for (let i = 0; i < shareResponses.length; i++) {
            const shareRes = shareResponses[i];
            if (!shareRes.success) {
                continue;
            }

            sharedFileInfoArr.push({
                name: uploadedFilenames[i],
                // eslint-disable-next-line max-len
                url: `${user.server}/index.php/f/${shareRes.result.ocs.data.file_source}`,
            });
        }

        submitDialogResult(
            new DndEmailShareDialogResult({
                sharedFileInfoArr,
            }),
        );

        if (shareResponses.some(isResponseError)) {
            dispatch(
                showNotification({
                    message: translate('upload_selected_error'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );
        }
    };
};

export const shareDndFilesWithMembersThunk = ({
    files,
    folder,
    members,
    permissions,
    onUploadProgress = () => {},
}: {
    files: FileItem[];
    folder: string;
    members: ResolvedChatMember[];
    permissions: SharePermissions;
    onUploadProgress?: (progress: number) => void;
}): ThunkAction<Promise<void>, RootState, unknown, AnyAction> => {
    return async function thunk(dispatch, getState): Promise<void> {
        const state = getState();
        const user = selectUser(state);
        const userId = selectUserId(state);
        const stubUrl = selectStubUrl(state);
        const translate = selectTranslate(state);
        if (!user || !userId || !stubUrl) {
            return;
        }

        const sharees = members.filter((member) => member.ncShareWith);
        if (sharees.length === 0) {
            return;
        }

        const isFolderCreated = await createShareFolders(folder);

        if (!isFolderCreated) {
            dispatch(
                showNotification({
                    message: translate('could_not_create_folder'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );
            return;
        }

        const requestConfig = {
            stubUrl,
            user,
            userId,
        } as const;

        const { uploadResponses, finalFilenames } = await uploadFilesInParallel(
            {
                files,
                folder,
                chunkSizeBytes:
                    state.appReducer.appConfig.chunkSizeMb * MEGABYTE,
                onUploadProgress,
                requestConfig,
            },
        );

        if (uploadResponses.some(isResponseError)) {
            logger.error(
                `uploadResults: ${JSON.stringify(uploadResponses)}`,
                'Upload',
            );
            dispatch(
                showNotification({
                    message: translate('upload_selected_error'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );

            if (uploadResponses.every(isResponseError)) {
                return;
            }
        }

        const uploadedFilenames = finalFilenames.filter(
            (filename, idx) => uploadResponses[idx].success,
        );

        const shareTasks = uploadedFilenames.flatMap((filename) =>
            sharees.map((member) => ({ filename, member })),
        );

        const taskResults = await mapInChunks(
            shareTasks,
            SHARE_REQUEST_CHUNK_SIZE,
            async ({ filename, member }) => ({
                filename,
                response: await nextcloudApi.shareByPath({
                    shareTargetPath: `${folder}/${filename}`,
                    shareType: ShareType.User,
                    shareWith: member.ncShareWith ?? '',
                    permissions: permissions,
                    requestConfig,
                }),
            }),
        );

        const shareResponses = taskResults.map((result) => result.response);

        if (shareResponses.some(isResponseError)) {
            logger.error(
                `shareResponses: ${JSON.stringify(shareResponses)}`,
                'Share',
            );
            dispatch(
                showNotification({
                    message: translate('group_share_partial_error'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );

            if (shareResponses.every(isResponseError)) {
                return;
            }
        }

        const sharedFileInfoArr: SharedFileInfo[] = [];
        for (const filename of uploadedFilenames) {
            const successful = taskResults.find(
                (result) =>
                    result.filename === filename && result.response.success,
            );
            if (!successful || !successful.response.success) {
                continue;
            }

            sharedFileInfoArr.push({
                name: filename,
                url: `${user.server}/index.php/f/${successful.response.result.ocs.data.file_source}`,
            });
        }

        submitDialogResult(
            new DndEmailShareDialogResult({
                sharedFileInfoArr,
            }),
        );
    };
};
